import { Link } from 'react-router-dom';
import { ChevronRight, Clock, Percent, Wallet } from 'lucide-react';
import PremiumCard from '../ui/PremiumCard.jsx';
import ScrollReveal from './ScrollReveal.jsx';
import { assetUrl } from '../../utils/assetUrl.js';
import publicCta from '../../content/publicCta.json';
import SectionTopWave from './SectionTopWave.jsx';

export default function CreditosSection({ items, loading, embedded = false, waveFromDark = false }) {
  const list = Array.isArray(items) ? items : [];
  const onLightSurface = !embedded;

  const content = (
    <>
        <ScrollReveal className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-accent-lime">Líneas de crédito</p>
          <h2
            className={
              embedded
                ? 'mt-3 font-display text-3xl font-bold text-white md:text-4xl'
                : 'mt-4 font-display text-3xl font-bold text-accent-blue md:text-4xl'
            }
          >
            Nuestras mejores tasas de interés para nuestros asociados
          </h2>
          <p className={embedded ? 'mt-4 text-slate-400' : 'mt-5 text-lg text-slate-600'}>
            Conoce las líneas de crédito disponibles, sus condiciones y solicita asesoría en nuestras oficinas.
          </p>
        </ScrollReveal>

        <div className={`grid gap-8 md:grid-cols-2 lg:grid-cols-3 ${embedded ? 'mt-10' : 'mt-12'}`}>
          {loading &&
            Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className={`h-80 animate-skeleton-shimmer rounded-2xl ring-1 ${embedded ? 'bg-white/10 ring-white/10' : 'bg-slate-200/90 ring-slate-200'}`}
              />
            ))}

          {!loading &&
            list.map((c, i) => (
              <ScrollReveal key={c.id} delayMs={Math.min(i, 5) * 55} className="min-w-0">
                <Link
                  to={`/creditos/${c.id}`}
                  className={`group block rounded-2xl no-underline text-inherit outline-none focus-visible:ring-2 focus-visible:ring-[#1acb12] focus-visible:ring-offset-2 ${embedded ? 'focus-visible:ring-offset-brand-navy' : 'focus-visible:ring-offset-white'}`}
                  aria-label={`${c.titulo}: ver detalle de la línea de crédito`}
                >
                  <PremiumCard hover solid={onLightSurface} className="flex h-full cursor-pointer flex-col">
                    <div className="relative mb-5 aspect-[16/10] overflow-hidden rounded-xl bg-slate-900 ring-1 ring-white/10">
                      {c.imagen ? (
                        <img
                          src={assetUrl(c.imagen)}
                          alt=""
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-xs font-medium uppercase tracking-wider text-slate-500">
                          Sin imagen
                        </div>
                      )}
                      {c.categoria ? (
                        <span className="absolute left-3 top-3 rounded-full bg-brand-navy px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white ring-1 ring-[#1acb12]/45">
                          {c.categoria}
                        </span>
                      ) : null}
                    </div>
                    <div className="flex flex-1 flex-col">
                      <h3 className="font-display text-xl font-semibold text-white">{c.titulo}</h3>
                      <p className="mt-2 line-clamp-3 flex-1 text-base leading-relaxed text-slate-400 sm:text-lg">{c.resumen}</p>
                      <ul className="mt-4 space-y-2 text-sm text-slate-400">
                        {c.tasa_interes ? (
                          <li className="flex items-center gap-2">
                            <Percent className="h-3.5 w-3.5 shrink-0 text-accent-lime" aria-hidden />
                            <span>
                              <span className="font-semibold text-slate-500">Interés: </span>
                              {c.tasa_interes}
                            </span>
                          </li>
                        ) : null}
                        {c.monto_maximo ? (
                          <li className="flex items-center gap-2">
                            <Wallet className="h-3.5 w-3.5 shrink-0 text-accent-lime" aria-hidden />
                            {c.monto_maximo}
                          </li>
                        ) : null}
                        {c.plazo ? (
                          <li className="flex items-center gap-2">
                            <Clock className="h-3.5 w-3.5 shrink-0 text-accent-lime" aria-hidden />
                            {c.plazo}
                          </li>
                        ) : null}
                      </ul>
                      <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent-lime transition group-hover:text-white">
                        {publicCta.creditosCardLinkLabel}{' '}
                        <ChevronRight className="h-4 w-4" aria-hidden />
                      </span>
                    </div>
                  </PremiumCard>
                </Link>
              </ScrollReveal>
            ))}
        </div>

        {!loading && list.length > 0 && (
          <ScrollReveal className="mt-12 text-center">
            <Link
              to="/creditos"
              className={
                embedded
                  ? 'inline-flex items-center gap-2 rounded-full border border-[#1acb12]/40 bg-black/20 px-5 py-2.5 text-sm font-semibold text-accent-lime backdrop-blur-sm transition hover:border-[#1acb12]/70 hover:bg-black/30 hover:text-white'
                  : 'inline-flex items-center gap-2 rounded-full border border-[#1acb12]/40 bg-white px-5 py-2.5 text-sm font-semibold text-accent-lime shadow-sm transition hover:border-[#1acb12]/70 hover:text-[#0d2b7f]'
              }
            >
              {publicCta.creditosSeeAllLabel}
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Link>
          </ScrollReveal>
        )}

        {!loading && list.length === 0 && (
          <p className={`mt-10 text-center ${embedded ? 'text-slate-400' : 'text-slate-600'}`}>
            No hay líneas de crédito publicadas por ahora.
          </p>
        )}
    </>
  );

  if (embedded) {
    return <div id="creditos">{content}</div>;
  }

  return (
    <section
      id="creditos"
      className={`relative isolate scroll-mt-24 overflow-hidden bg-white py-16 lg:py-24 ${waveFromDark ? 'border-t-0 pt-14 lg:pt-16' : 'border-t border-slate-200'}`}
    >
      {waveFromDark && <SectionTopWave />}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-[2] h-1 bg-gradient-to-r from-transparent via-[#1acb12] to-transparent"
        aria-hidden
      />
      <div className="relative z-[2] mx-auto max-w-7xl px-6 lg:px-8">{content}</div>
    </section>
  );
}
